import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';

import axios, { type AxiosResponse } from 'axios';

import { Col, Panel, Row } from '@shared/components/bootstrap';
import { Percent } from '@shared/components/helpers/percent';
import { Picture } from '@shared/components/helpers/picture';
import { UserRole } from '@shared/types/user_role';
import { ImgixBG, ImgixFit } from '@shared/utils/imgix';

import { State as AccountState } from '@admin/components/accounts/state';
import { Formatter as CategoryFormatter } from '@admin/components/categories/formatter';
import { Labels as DimensionsLabels } from '@admin/components/dimensions/labels';
import { Roles } from '@admin/components/helpers/roles';
import { Titleize } from '@admin/components/helpers/titleize';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { IncidentsPanel } from '@admin/components/incidents/panel';
import { LogisticsPalletInfoPanels } from '@admin/components/logistics/pallets/logistics_pallet_info_panels';
import { NotesPanel } from '@admin/components/notes/panel';
import { OpsAuditResultsPanel } from '@admin/components/ops_audit/audit_results/audit_results_panel';
import { PalletImagesPanel } from '@admin/components/pallet_images/pallet_images_panel';
import { PalletRequestButton } from '@admin/components/pallet_requests/pallet_request_button';
import { QaQrCodeLink } from '@admin/components/qa_automation/qa_qr_code_link';
import { Spinner } from '@admin/components/spinner';
import { CreateTicket as CreateTechSupportTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { Panel as TimelineActivityPanel } from '@admin/components/timeline_activity/panel';
import { useAccountRequest } from '@admin/hooks/use_account_request';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { useTitle } from '@admin/hooks/use_title';
import { History__Subject, NoteNotableEnum, Ticket__TicketableEnum } from '@admin/schema';

type Account = {
  id: number;
  state: 'active' | 'pending' | 'canceled' | 'suspended';
  customer: {
    id: number;
    name: string;
  };
};

type Image = {
  id: number;
  source: string;
  state: string;
};

type Item = {
  id: number;
  hero: number;
  account_id: number;
  length?: number;
  width?: number;
  height?: number;
  dimensions_overwritten?: boolean;
  custom_category_name?: string;
  sizing_selection?: string;
  state: string;
  barcode: {
    value: string;
  };
  images: Image[];
};

type Pallet = {
  id: number;
  state: string;
  barcode: {
    value: string;
  };
  warehouse: {
    id: number;
    name: string;
    tz: string;
  };
  type: {
    name: string;
  };
  location?: {
    id: number;
    value: string;
  };
  grade?: {
    score: 'good' | 'acceptable' | 'fail';
    explanation?: string;
    image?: Image;
  };
  height?: {
    value: number;
    unit: string;
  };
  density_metrics: {
    pallet_cuft: number;
    item_cuft: number;
    percentage: number;
  };
  active_transfer?: {
    id: number;
    vehicle?: {
      id: number;
      serial_code: string;
    };
  };
  items_without_skus: Item[];
  items_count: number;
  customer_count: number;
};

const CUFT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const usePalletRequest = (id: string | number) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Pallet>> | undefined>(undefined);

  useEffect(() => {
    setRequest(axios.get(`/pallets/${id}.json`));
  }, [id]);

  return useAxiosRequest(request);
};

const groupByAccountID = <T extends { account_id: number }>(records: T[]) => {
  const result = new Map<number, T[]>();
  for (const item of records) {
    if (!result.has(item.account_id)) result.set(item.account_id, []);
    result.get(item.account_id)?.push(item);
  }
  return result;
};

const PalletDetailsAccount: React.FC<{
  accountID: number;
  items: Item[];
}> = ({ accountID, items }) => {
  const { response } = useAccountRequest(accountID);

  const account = response?.data;

  return (
    <>
      <Panel>
        <Panel.Body>
          <span>Customer:</span>
          <span> </span>
          <a className="text-primary" href={`/accounts/${accountID}`}>
            {account?.customer.name ?? '-'}
          </a>
          <span> </span>
          {account && <AccountState account={account} />}
        </Panel.Body>
      </Panel>
      <Row>
        {items.map((item) => {
          const hero = item.images[item.hero];
          return (
            <Col key={item.id} xs={6} sm={4} md={3} lg={2}>
              <div className="panel brick">
                <a href={`/accounts/${accountID}/items/${item.id}`}>
                  <figure className="brick-square">
                    <Picture
                      source={hero && hero.state === 'uploaded' ? hero.source : undefined}
                      width={480}
                      height={480}
                      className="brick-image"
                      background={ImgixBG.White}
                      fit={ImgixFit.Fill}
                    />
                  </figure>
                </a>
                <div className="brick-metadata">
                  <div>
                    <span>#{item.barcode.value}</span>
                    <span> </span>
                    <strong>({item.state})</strong>
                    <QaQrCodeLink value={item.barcode.value} />
                  </div>
                  <div>
                    <span className="label label-default">
                      <CategoryFormatter resource={item} fallback="No Category" />
                    </span>
                  </div>
                  <div>
                    <DimensionsLabels resource={item} />
                  </div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </>
  );
};

export const PalletDetails: React.FC = () => {
  useTitle('Pallet');
  const { id } = useParams<{ id: string }>();
  const { response, loading } = usePalletRequest(id);
  if (loading) return <Spinner />;

  if (!response) return null;

  const pallet = response.data;
  const timezone = pallet.warehouse.tz;

  const items = pallet.items_without_skus;
  const itemsByAccountID = groupByAccountID(items);
  const accountIDs = Array.from(itemsByAccountID.keys()).sort();

  return (
    <>
      <Roles show={[UserRole.Admin, UserRole.L2Agent, UserRole.Manager]}>
        <div className="text-right mar-btm">
          <CreateTechSupportTicket
            ticketable={{
              id: pallet.id,
              type: Ticket__TicketableEnum.Pallet,
              displayName: 'Pallet #' + pallet.barcode.value,
            }}
          />
        </div>
      </Roles>

      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <Row>
            <Col md={6}>
              <p>
                <strong>Barcode:</strong> <span>#{pallet.barcode.value}</span>
                <span> </span>
                <QaQrCodeLink value={pallet.barcode.value} />
              </p>
              <p>
                <strong>Type:</strong>
                <span> </span>
                <span>{pallet.type.name}</span>
              </p>
              <p>
                <strong>State:</strong>
                <span> </span>
                <span>
                  <Titleize text={pallet.state} />
                </span>
              </p>
              <p>
                <strong>Grade:</strong>
                <span> </span>
                <span>
                  <Titleize text={pallet.grade ? pallet.grade.score : 'ungraded'} />
                </span>
                <span> </span>
                {pallet.grade?.explanation && <span>{pallet.grade.explanation}</span>}
              </p>
              {pallet.height && (
                <p>
                  <strong>Height:</strong>
                  <span> </span>
                  <span>
                    {pallet.height.value} {pallet.height.unit}
                  </span>
                </p>
              )}
              {['Single', 'Double', 'Stackable Crate'].includes(pallet.type.name) && (
                <p>
                  <strong>Density:</strong>
                  <span> </span>
                  <span>
                    <Percent value={Number(pallet.density_metrics.percentage)} />
                    <span> </span>
                    <span>(</span>
                    <span>{CUFT_FORMATTER.format(Number(pallet.density_metrics.item_cuft))} item cuft</span>
                    <span> / </span>
                    <span>{CUFT_FORMATTER.format(Number(pallet.density_metrics.pallet_cuft))} pallet cuft</span>
                    <span>)</span>
                  </span>
                </p>
              )}
              {pallet.location && (
                <p>
                  <strong>Location:</strong>
                  <span> </span>
                  <a className="text-primary" href={`/locations/${pallet.location.id}`}>
                    <span>#{pallet.location.value}</span>
                  </a>
                  <span> </span>
                  <QaQrCodeLink value={pallet.location.value} />
                </p>
              )}
              {!pallet.location && pallet.active_transfer && pallet.active_transfer.vehicle && (
                <p>
                  <strong>Location:</strong>
                  <span> </span>
                  <a className="text-primary" href={`/transfers/${pallet.active_transfer.id}`}>
                    <span>Transfer Truck #{pallet.active_transfer.vehicle.serial_code}</span>
                  </a>
                </p>
              )}

              <p>
                <strong>Item Count:</strong>
                <span> </span>
                <span>{pallet.items_count}</span>
              </p>
              <p>
                <strong>Customer Count:</strong>
                <span> </span>
                <span>{pallet.customer_count}</span>
              </p>
            </Col>
            <Col md={6}>
              {pallet.grade?.image && (
                <Picture
                  className="pull-right"
                  height={250}
                  width={250}
                  source={pallet.grade.image.state === 'uploaded' ? pallet.grade.image.source : undefined}
                  background={ImgixBG.White}
                  fit={ImgixFit.Fill}
                />
              )}
            </Col>
          </Row>
        </Panel.Body>
        {pallet.state === 'stored' && (
          <Panel.Footer>
            <PalletRequestButton palletID={id} />
          </Panel.Footer>
        )}
      </Panel>

      <LogisticsPalletInfoPanels id={id} />
      <TimelineActivityPanel resource={{ id: pallet.id, type: 'pallets' }} timezone={timezone} />

      <HistoriesPanel type={History__Subject.Pallet} id={id} timezone={timezone} />

      <IncidentsPanel kind="pallet" parentType="pallets" parentID={id} />

      {accountIDs.map((accountID) => (
        <PalletDetailsAccount key={accountID} accountID={accountID} items={itemsByAccountID.get(accountID) ?? []} />
      ))}

      <NotesPanel type={NoteNotableEnum.Pallet} id={id} />

      <PalletImagesPanel palletID={id} timezone={timezone} />
      <OpsAuditResultsPanel resourceType="Pallet" resourceID={id} />
    </>
  );
};
